import { useCallback, useRef, useState } from "react";
import { DateTime } from "luxon";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { Document, Thumbnail } from "react-pdf";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  FileAttachmentIcon,
  Download01Icon,
  PrinterIcon,
  Delete01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useNotesController } from "shared/queries/useNotesController";
import { NoteAttachment } from "shared/app/Notes/types";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { Show } from "shared/components";
import { toast } from "react-toastify";
import { AutoLoadMoreOnScroll } from "./AutoLoadMoreOnScroll";
import { cx } from "shared/utils";
import { DotsHorizontal } from "untitledui-js-base";
import {
  IconButton,
  IconButtonVariant,
  IconButtonSize
} from "shared/components/IconButton";
import { Loader } from "shared/components/Loader";
import { AssetViewer } from "shared/app/AssetViewer";

interface PreviewPos {
  top: number;
  left: number;
}

const FilePreviewContent = ({
  file,
  isLoading,
  isError
}: {
  file: NoteAttachment;
  isLoading: boolean;
  isError: boolean;
}) => {
  const isImage = file.content_type.startsWith("image/");

  if (isError)
    return (
      <div className="flex w-[276px] items-center justify-center bg-background-900 py-10">
        <p className="text-sm text-content-300">Failed to load document</p>
      </div>
    );
  if (isLoading)
    return (
      <div className="flex w-[276px] items-center justify-center bg-background-900 py-10">
        <Loader />
      </div>
    );
  if (isImage)
    return (
      <img
        src={file.url}
        alt={file.original_filename}
        className="w-[276px] h-auto object-contain bg-background-900"
      />
    );
  return (
    <div className="w-[276px] flex items-center justify-center bg-background-900">
      <Document
        key={`pdf_${file.url}`}
        loading={<Loader />}
        file={file.url}
        className="pointer-events-none"
      >
        <Thumbnail width={276} pageNumber={1} />
      </Document>
    </div>
  );
};

const FilePreviewPopover = ({
  file,
  pos,
  previewSide,
  isLoading,
  isError
}: {
  file: NoteAttachment;
  pos: PreviewPos;
  previewSide: "left" | "right";
  isLoading: boolean;
  isError: boolean;
}) => {
  return createPortal(
    <div
      style={{ top: pos.top, left: pos.left }}
      className={cx(
        "fixed z-[9999] rounded-xl border border-alpha-100 bg-background-1000 shadow-lg overflow-hidden pointer-events-none",
        previewSide === "left" ? "-translate-x-full" : ""
      )}
    >
      <FilePreviewContent file={file} isLoading={isLoading} isError={isError} />
    </div>,
    document.body
  );
};

const POPOVER_HEIGHT = 332;

const FileRow = ({
  file,
  previewSide = "left",
  onDownload,
  onPrint,
  onDelete
}: {
  file: NoteAttachment;
  previewSide?: "left" | "right";
  onDownload: (url: string, filename: string) => void;
  onPrint: (url: string, contentType: string) => void;
  onDelete: (attachmentId: number) => void;
}) => {
  const [previewPos, setPreviewPos] = useState<PreviewPos | null>(null);
  const [isAssetViewerOpen, setIsAssetViewerOpen] = useState(false);
  const fileNameButtonRef = useRef<HTMLButtonElement>(null);
  const { data, isLoading, isError } = useQuery({
    queryFn: () => fetch(file.url).then((response) => response.blob()),
    queryKey: ["NOTES_FILE_PREVIEW", file.url]
  });

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!fileNameButtonRef.current) return;
    const rect = fileNameButtonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= POPOVER_HEIGHT
        ? rect.bottom + 6
        : rect.top - POPOVER_HEIGHT - 6;
    const left = previewSide === "left" ? e.clientX - 8 : e.clientX + 8;
    setPreviewPos({ top, left });
  };

  const handleMouseLeave = () => setPreviewPos(null);
  const handleOpenAssetViewer = () => {
    setPreviewPos(null);
    setIsAssetViewerOpen(true);
  };

  return (
    <tr className="border-b border-alpha-80">
      <td
        className="py-3 cursor-pointer max-w-[200px]"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          ref={fileNameButtonRef}
          type="button"
          className="w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-primary-500 underline block text-left"
          onClick={handleOpenAssetViewer}
        >
          {file.original_filename}
        </button>
        {previewPos && !isAssetViewerOpen ? (
          <FilePreviewPopover
            file={file}
            pos={previewPos}
            previewSide={previewSide}
            isLoading={isLoading}
            isError={isError}
          />
        ) : null}
        {data ? (
          <AssetViewer
            src={file.url}
            filename={file.original_filename}
            open={isAssetViewerOpen}
            onClose={() => setIsAssetViewerOpen(false)}
            mimeType={file.content_type || data.type}
            fileSize={file.size || data.size}
          />
        ) : null}
      </td>
      <td className="px-4 py-5 whitespace-nowrap space-y-2">
        <Show if={file.note_author_name}>
          <p className="text-sm font-medium text-content-1000">
            {file.note_author_name}
          </p>
        </Show>
        <p className="text-xs text-content-400">
          {DateTime.fromISO(file.note_created_at).toFormat(
            "MMM d, yyyy 'at' h:mm a"
          )}
        </p>
      </td>
      <td className="px-4 py-3 text-right">
        <ContextMenuPopover
          placement="bottom-end"
          contentClassName="!z-[999999] shadow-xl w-[189px] [&>*]:!min-w-0"
          trigger={
            <IconButton
              variant={IconButtonVariant.BASE}
              size={IconButtonSize.SM}
              className="text-content-200"
              Icon={DotsHorizontal}
              borderLess
            />
          }
          actions={[
            {
              title: (
                <span className="flex items-center gap-2">
                  <HugeiconsIcon
                    icon={Download01Icon}
                    size={20}
                    strokeWidth={1.5}
                    className="text-content-200"
                  />
                  Download
                </span>
              ),
              onClick: () => onDownload(file.url, file.original_filename)
            },
            {
              title: (
                <span className="flex items-center gap-2">
                  <HugeiconsIcon
                    icon={PrinterIcon}
                    size={20}
                    strokeWidth={1.5}
                    className="text-content-200"
                  />
                  Print
                </span>
              ),
              onClick: () => onPrint(file.url, file.content_type)
            },
            {
              title: (
                <span className="flex items-center gap-2">
                  <HugeiconsIcon
                    icon={Delete01Icon}
                    size={20}
                    strokeWidth={1.5}
                    className="text-content-200"
                  />
                  Delete
                </span>
              ),
              color: "text-error-500",
              onClick: () => onDelete(file.id)
            }
          ]}
        />
      </td>
    </tr>
  );
};

export const NotesAttachmentsTable = ({
  notesController,
  previewSide = "left",
  columnsClassName
}: {
  notesController: ReturnType<typeof useNotesController>;
  previewSide?: "left" | "right";
  columnsClassName?: { name?: string; uploaded?: string };
}) => {
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { allAttachments, attachmentsMeta, deleteAttachmentMeta } =
    notesController;

  const handleDelete = useCallback(
    (attachmentId: number) => {
      deleteAttachmentMeta.mutate(attachmentId);
    },
    [deleteAttachmentMeta]
  );

  const handleDownload = useCallback(async (url: string, filename: string) => {
    try {
      const res = await fetch(url, { cache: "no-cache" });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Failed to download file");
    }
  }, []);

  const handlePrint = useCallback(async (url: string, contentType: string) => {
    try {
      const res = await fetch(url, { cache: "no-cache" });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(
        new Blob([blob], { type: contentType })
      );
      if (iframeRef.current) {
        iframeRef.current.src = blobUrl;
        iframeRef.current.onload = () => {
          iframeRef.current?.contentWindow?.focus();
          iframeRef.current?.contentWindow?.print();
          URL.revokeObjectURL(blobUrl);
        };
      }
    } catch {
      toast.error("Failed to print file");
    }
  }, []);

  if (!allAttachments.length)
    return (
      <div className="flex flex-auto flex-col items-center justify-center">
        <div className="mb-6 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-background-800 p-5">
          <HugeiconsIcon
            icon={FileAttachmentIcon}
            size={32}
            strokeWidth={1.5}
            className="text-content-400"
          />
        </div>
        <p className="text-base font-bold text-content-500">No files yet</p>
        <p className="text-sm font-medium text-content-300">
          Attach files to notes to see them here
        </p>
      </div>
    );

  return (
    <div
      ref={scrollRootRef}
      className="size-full flex-grow overflow-x-hidden overflow-y-auto bg-background-1000 py-2 pl-6"
    >
      <table className="w-full border-collapse table-fixed">
        <thead className="h-11">
          <tr className="border-b border-alpha-100">
            <th
              className={cx(
                "py-2 text-left text-xs font-medium text-content-300 w-[200px]",
                columnsClassName?.name
              )}
            >
              Name
            </th>
            <th
              className={cx(
                "px-4 py-2 text-left text-xs font-medium text-content-300 w-[200px]",
                columnsClassName?.uploaded
              )}
            >
              Uploaded
            </th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody>
          {allAttachments.map((file) => (
            <FileRow
              key={file.id}
              file={file}
              previewSide={previewSide}
              onDownload={handleDownload}
              onPrint={handlePrint}
              onDelete={handleDelete}
            />
          ))}
        </tbody>
      </table>
      <iframe
        ref={iframeRef}
        title="print-frame"
        style={{
          position: "absolute",
          left: "-9999px",
          width: "1px",
          height: "1px"
        }}
      />
      <AutoLoadMoreOnScroll
        rootRef={scrollRootRef}
        hasNextPage={Boolean(attachmentsMeta.hasNextPage)}
        isFetchingNextPage={attachmentsMeta.isFetchingNextPage}
        onLoadMore={() => attachmentsMeta.fetchNextPage()}
      />
    </div>
  );
};
